import { useImmer } from "use-immer";
import { HistoryState } from "../utils/planUtils";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export function useDrawerHistory() {
  const [history, updateHistory] = useImmer<HistoryState[]>([]);
  const [windowHistoryStackSize, setWindowHistoryStackSize] = useImmer(
    window.history.length,
  );
  const pendingCloses = useRef(0);

  const pushHistory = (s: HistoryState) => {
    updateHistory((historyDraft) => {
      historyDraft.push(s);
    });
  };
  const popHistory = () => {
    updateHistory((historyDraft) => {
      historyDraft.pop();
    });
  };

  const navigate = useNavigate();

  // Mehrere Drawer-Schließungen im selben Tick (z. B. Save schließt Child- und
  // Parent-Drawer) können sich nicht auf mehrere history.go(-1) verlassen –
  // der Browser führt nur den ersten aus. Deshalb wird hier eine Queue
  // geführt: jeder Popstate triggert den nächsten Schritt zurück.
  const closeDrawer = () => {
    pendingCloses.current += 1;
    if (pendingCloses.current === 1) {
      navigate(-1);
    }
  };

  const clearHistory = () => {
    return new Promise<void>((resolve, reject) => {
      function handlePopState(e: PopStateEvent) {
        if (e.state.usr?.id) {
          goBack();
        } else {
          window.removeEventListener("popstate", handlePopState);
          resolve();
        }
      }

      function goBack() {
        navigate(-1);
      }

      try {
        window.addEventListener("popstate", handlePopState);
        goBack();

        updateHistory((historyDraft) => {
          historyDraft.splice(0, historyDraft.length);
        });
      } catch (e) {
        reject(e);
      }
    });
  };
  const hasHistory = (s: HistoryState["id"]) => {
    return !!history.find((l) => l.id == s);
  };

  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      const isBackwards = window.history.length <= windowHistoryStackSize;
      if (isBackwards) {
        popHistory();
      }
      setWindowHistoryStackSize(window.history.length);
      if (pendingCloses.current > 0) {
        pendingCloses.current -= 1;
        if (pendingCloses.current > 0) {
          navigate(-1);
        }
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [history]);
  return {
    history,
    pushHistory,
    popHistory,
    clearHistory,
    closeDrawer,
    hasHistory,
    setWindowHistoryStackSize,
  };
}
